import { queryClient } from '@/query/queryClient';
import {
  SESSION_ROOT,
  activeAssignmentId,
  headerSelectionKey,
  isHeaderSelectionKey,
  sessionKeys,
} from '@/query/keys';

/**
 * The CURRENT HEADER SELECTION, read and written outside React (UI state
 * plan, step A): the header brand, the header partner and the global filter
 * (dates, currency, countries, brands, partners, ...).
 *
 * It lives in ONE place, the TanStack Query cache, in page memory only and
 * under the ACTIVE role assignment (`['a', <assignment id>, 'header-selection']`):
 * a role switch or a sign-out drops it with the rest of the role's data
 * (auth/clearSession.ts). Nothing here touches browser storage: the keys
 * `brand_id`, `brand_id_object`, `property_id`, `property_data` and
 * `globalsearch` are gone (lint rule + selection/selectionKeysGuard.test.ts).
 *
 * A hard refresh starts empty, so the last selection is kept on the SERVER
 * per user and role assignment (owner 2026-10-12): it is loaded with the
 * session before pages render (ApiProvider.loadHeaderSelection: page load,
 * sign-in, role switch) and saved, debounced, whenever it changes.
 *
 * React reads it through `useHeaderSelection()` (selection/useHeaderSelection.ts)
 * or the existing `useGlobalFilters()`; code that cannot use a hook (the static
 * ApiProvider, auth/*.ts, event handlers that need the value of the moment)
 * uses the accessors below.
 *
 * No React and no ApiProvider import here (ApiProvider imports this module).
 */

/** A header brand / partner as the header lists answer it (`id` + display fields). */
export type HeaderEntity = { id: string | number; [key: string]: any };

/** One entry of the global filter (`filter_type` + the chosen value). */
export type HeaderFilter = { filter_type: string; [key: string]: any };

export interface HeaderSelection {
  /** The header brand (was `brand_id_object`), or null. */
  readonly brand: HeaderEntity | null;
  /** `String(brand.id)` (was `brand_id`), or null. */
  readonly brandId: string | null;
  /** The header partner (was `property_data`), or null. */
  readonly property: HeaderEntity | null;
  /** `String(property.id)` (was `property_id`), or null. */
  readonly propertyId: string | null;
  /** The global filter (was `globalsearch`); null = none chosen yet (the header uses its defaults). */
  readonly filters: readonly HeaderFilter[] | null;
}

/** What the server keeps: the three chosen parts. */
export interface StoredHeaderSelection {
  brand: HeaderEntity | null;
  property: HeaderEntity | null;
  filters: HeaderFilter[] | null;
}

interface Entry {
  selection: HeaderSelection;
  /** The server answered for this assignment (a record or "none"): changes are saved from now on. */
  loaded: boolean;
}

export const EMPTY_SELECTION: HeaderSelection = Object.freeze({
  brand: null,
  brandId: null,
  property: null,
  propertyId: null,
  filters: null,
});

/** An independent copy, as a round trip through storage gave before (plain JSON data). */
const copy = <T>(value: T): T =>
  value === null || value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);

const entityOrNull = (value: unknown): HeaderEntity | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const id = (value as HeaderEntity).id;
  if (id === null || id === undefined || id === '' || id === 'clear') return null;
  return copy(value as HeaderEntity);
};

const filtersOrNull = (value: unknown): HeaderFilter[] | null =>
  Array.isArray(value)
    ? copy(value.filter((item) => item !== null && typeof item === 'object'))
    : null;

const toSelection = (parts: Partial<StoredHeaderSelection>): HeaderSelection => {
  const brand = entityOrNull(parts.brand);
  const property = entityOrNull(parts.property);
  return {
    brand,
    brandId: brand ? String(brand.id) : null,
    property,
    propertyId: property ? String(property.id) : null,
    filters: filtersOrNull(parts.filters),
  };
};

const sameParts = (a: HeaderSelection, b: HeaderSelection): boolean =>
  JSON.stringify([a.brand, a.property, a.filters]) ===
  JSON.stringify([b.brand, b.property, b.filters]);

const readEntry = (assignmentId: number | null): Entry | undefined =>
  queryClient.getQueryData<Entry>(headerSelectionKey(assignmentId));

const writeEntry = (assignmentId: number | null, entry: Entry): void => {
  const key = headerSelectionKey(assignmentId);
  // never garbage-collected while the page lives (as the session entries)
  queryClient.setQueryDefaults(key, { gcTime: Infinity });
  queryClient.setQueryData(key, entry);
};

// ---- reading -------------------------------------------------------------------------

/**
 * The selection of the active assignment. The SAME object until something
 * changes (React snapshot): never mutate it; the accessors below hand out copies.
 */
export const getHeaderSelection = (
  assignmentId: number | null = activeAssignmentId(),
): HeaderSelection => readEntry(assignmentId)?.selection ?? EMPTY_SELECTION;

/** The header brand id as text (what the `brand_id` storage key answered), or null. */
export const getHeaderBrandId = (): string | null => getHeaderSelection().brandId;

/** A copy of the header brand (what the parsed `brand_id_object` key answered), or null. */
export const getHeaderBrand = <T extends HeaderEntity = HeaderEntity>(): T | null =>
  copy(getHeaderSelection().brand) as T | null;

/** The header partner id as text (was `property_id`), or null. */
export const getHeaderPropertyId = (): string | null => getHeaderSelection().propertyId;

/** A copy of the header partner (was `property_data`), or null. */
export const getHeaderProperty = <T extends HeaderEntity = HeaderEntity>(): T | null =>
  copy(getHeaderSelection().property) as T | null;

/** A copy of the global filter (was the parsed `globalsearch` key); null = none chosen yet. */
export const getHeaderFilters = <T extends HeaderFilter = HeaderFilter>(): T[] | null =>
  copy(getHeaderSelection().filters) as T[] | null;

/** A saved filter combination (JSON text, as the server keeps it) as a filter list, else null. */
export const parseHeaderFilters = (json: unknown): HeaderFilter[] | null => {
  if (Array.isArray(json)) return filtersOrNull(json);
  if (typeof json !== 'string' || json.trim() === '') return null;
  try {
    return filtersOrNull(JSON.parse(json));
  } catch {
    return null;
  }
};

// ---- writing -------------------------------------------------------------------------

const change = (parts: Partial<StoredHeaderSelection>): void => {
  const assignmentId = activeAssignmentId();
  const entry = readEntry(assignmentId);
  const current = entry?.selection ?? EMPTY_SELECTION;
  const next = toSelection({
    brand: current.brand,
    property: current.property,
    filters: current.filters as HeaderFilter[] | null,
    ...parts,
  });
  if (sameParts(current, next)) return;
  writeEntry(assignmentId, { selection: next, loaded: entry?.loaded ?? false });
  scheduleSave(assignmentId);
};

/** Sets the header brand; null (or the "Remove Filter" entry, id `clear`) clears it. */
export const setHeaderBrandSelection = (brand: HeaderEntity | null | undefined): void =>
  change({ brand: brand ?? null });

/** Sets the header partner; null (or id `clear`) clears it. */
export const setHeaderPropertySelection = (property: HeaderEntity | null | undefined): void =>
  change({ property: property ?? null });

/** Sets the global filter (a list, or a saved combination's JSON text); null removes it. */
export const setHeaderFilters = (filters: readonly unknown[] | string | null | undefined): void =>
  change({ filters: parseHeaderFilters(filters) });

/** Nothing selected any more under the active assignment. */
export const clearHeaderSelection = (): void =>
  change({ brand: null, property: null, filters: null });

// ---- the server copy -----------------------------------------------------------------

/** The server answered for this assignment in this page (nothing to load). */
export const isHeaderSelectionLoaded = (
  assignmentId: number | null = activeAssignmentId(),
): boolean => readEntry(assignmentId)?.loaded === true;

/**
 * Puts the selection the server keeps (or, without a record, the defaults)
 * into memory. `loaded`: the server answered, so later changes are saved;
 * false when the load failed (the selection then stays in memory only: what
 * could not be read is never overwritten). Does not save.
 */
export const hydrateHeaderSelection = (
  parts: Partial<StoredHeaderSelection> | null,
  assignmentId: number | null = activeAssignmentId(),
  loaded = true,
): void => {
  writeEntry(assignmentId, { selection: toSelection(parts ?? {}), loaded });
};

/** The three parts as the server stores them. */
export const toStoredSelection = (selection: HeaderSelection): StoredHeaderSelection => ({
  brand: copy(selection.brand),
  property: copy(selection.property),
  filters: copy(selection.filters) as HeaderFilter[] | null,
});

/** Sends one selection to the server; resolves when it answered (never rejects for the caller). */
export type HeaderSelectionSaver = (
  // eslint-disable-next-line no-unused-vars
  selection: StoredHeaderSelection,
  // eslint-disable-next-line no-unused-vars
  assignmentId: number,
) => Promise<unknown>;

/** Changes are sent this long after the last one. */
export const SAVE_DELAY_MS = 600;

let saver: HeaderSelectionSaver | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let saveFor: number | null = null;
let saving: Promise<unknown> = Promise.resolve();

/** ApiProvider registers the request that saves a selection (it cannot be imported here). */
export const setHeaderSelectionSaver = (next: HeaderSelectionSaver | null): void => {
  saver = next;
};

const sendNow = (assignmentId: number): Promise<unknown> => {
  // still this assignment's page, and the server's copy was read
  if (!saver || activeAssignmentId() !== assignmentId) return saving;
  const entry = readEntry(assignmentId);
  if (!entry?.loaded) return saving;
  const send = saver;
  const selection = toStoredSelection(entry.selection);
  // one after the other: the last change is the last one stored
  saving = saving.then(() => send(selection, assignmentId)).catch(() => undefined);
  return saving;
};

function scheduleSave(assignmentId: number | null): void {
  if (assignmentId === null) return;
  if (saveTimer !== null) clearTimeout(saveTimer);
  saveFor = assignmentId;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    saveFor = null;
    void sendNow(assignmentId);
  }, SAVE_DELAY_MS);
}

/**
 * Sends a change that is still waiting, and waits for the server (before a
 * page reload that would lose it: the end of a role switch).
 */
export const flushHeaderSelection = (): Promise<unknown> => {
  if (saveTimer === null || saveFor === null) return saving;
  clearTimeout(saveTimer);
  const assignmentId = saveFor;
  saveTimer = null;
  saveFor = null;
  return sendNow(assignmentId);
};

/** Drops a change that is still waiting (sign-out, role switch: auth/clearSession.ts). */
export const cancelHeaderSelectionSave = (): void => {
  if (saveTimer !== null) clearTimeout(saveTimer);
  saveTimer = null;
  saveFor = null;
};

/** A change is waiting to be sent. */
export const isHeaderSelectionSavePending = (): boolean => saveTimer !== null;

// ---- following it (React: selection/useHeaderSelection.ts) ---------------------------

const listeners = new Set<() => void>();
let stopWatchingCache: (() => void) | null = null;

const concernsSelection = (key: readonly unknown[]): boolean =>
  isHeaderSelectionKey(key) ||
  (key[0] === SESSION_ROOT && key[1] === sessionKeys.activeAssignment[1]);

/**
 * Calls `listener` whenever the selection of the active assignment may have
 * changed: its cache entry, or the active assignment itself.
 */
export const subscribeHeaderSelection = (listener: () => void): (() => void) => {
  listeners.add(listener);
  stopWatchingCache ??= queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'added' && event.type !== 'removed' && event.type !== 'updated') return;
    if (!concernsSelection(event.query.queryKey)) return;
    listeners.forEach((l) => l());
  });
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && stopWatchingCache) {
      stopWatchingCache();
      stopWatchingCache = null;
    }
  };
};

/** Test helper: no pending save, no in-flight save chain (the saver stays registered). */
export const resetHeaderSelectionForTests = (): void => {
  cancelHeaderSelectionSave();
  saving = Promise.resolve();
};
